import { getAccessToken, getGuId, getRefreshToken, getLoginId } from "../utils/SecurityService";
import { refreshAuthToken } from "./tokenRefresh";
import { dispatchInactiveUserEvent, isInactiveUserResponse } from "../utils/inactiveUserHandler.js";

const isValidStoredValue = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  value !== "null" &&
  value !== "undefined";

const hasRefreshContext = () =>
  isValidStoredValue(getRefreshToken()) &&
  isValidStoredValue(getGuId()) &&
  isValidStoredValue(getLoginId());

const buildAuthorizedHeaders = (headers = {}, accessToken = getAccessToken()) => {
  if (!isValidStoredValue(accessToken)) {
    return { ...headers };
  }

  return {
    ...headers,
    Authorization: `Bearer ${accessToken}`,
  };
};

const clearSessionAndRedirect = () => {
  sessionStorage.clear();
  localStorage.clear();
  window.location.href = "/";
};

const throwInactiveUserError = (payload) => {
  dispatchInactiveUserEvent(payload);

  const error = new Error(
    payload?.Data?.ErrorMessage || "This user is currently inactive."
  );
  error.name = "InactiveUserError";
  error.response = payload;
  throw error;
};


export async function loginPost(url, requestParams) {
  console.log("POST CALLED:", url, requestParams);
  console.log("Using Access Token:", getAccessToken());
  const headers = buildAuthorizedHeaders({ "Content-Type": "application/json" });
  const currentGuId = getGuId();
  if (currentGuId) requestParams.GuId = currentGuId;

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(requestParams),
  });

  console.log("POST raw response for url:", url, response);

  if (!response.ok) {
    const errorBody = await response.text();
    console.error("POST ERROR BODY:", errorBody);
    throw new Error("Request failed: " + response.status);
  }

  const payload = await response.json();

  /* if (isInactiveUserResponse(payload)) {
    throwInactiveUserError(payload);
  } */

  return payload;
}

export const post = async ({
  url,
  method = "POST",
  params = {},
}) => {
  // Common params for all APIs
  const finalParams = {
    ...params,
    GuId: getGuId(),
  };
  console.log("POST CALLED:", url, finalParams);

  const response = await fetchWithTokenRefresh(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: method === "GET" ? null : JSON.stringify(finalParams),
  });
  console.log("POST raw response for url:", url, response);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API failed (${response.status}): ${errorText}`);
  }

  const payload = await response.json();

  /* if (isInactiveUserResponse(payload)) {
    throwInactiveUserError(payload);
  } */

  return payload;
};

// Multipart POST for file uploads. No Content-Type header — the browser sets
// the multipart boundary automatically. Token refresh is handled by fetchWithTokenRefresh.
export const postMultipart = async ({ url, method = "POST", formData }) => {
  console.log("POST (multipart) CALLED:", url);

  const response = await fetchWithTokenRefresh(url, {
    method,
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API failed (${response.status}): ${errorText}`);
  }

  return response.json();
};

// This function only knows how to retry fetch when token expires
export const fetchWithTokenRefresh = async (url, options = {}, retry = true) => {

  if (!hasRefreshContext()) {
    window.location.href = "/";
    throw new Error("Tokens missing");
  }

  let currentAccessToken = getAccessToken();

  // No access token yet (e.g. right after boot) — mint one first (shared single-flight).
  if (!isValidStoredValue(currentAccessToken)) {
    try {
      const refreshedTokens = await refreshAuthToken();
      currentAccessToken = refreshedTokens.accessToken;
    } catch (e) {
      clearSessionAndRedirect();
      throw e;
    }
  }

  let response = await fetch(url, {
    ...options,
    headers: buildAuthorizedHeaders(options.headers || {}, currentAccessToken),
  });

  // ✅ If not unauthorized → return
  if (response.status !== 401) {
    return response;
  }

  // ❌ Prevent infinite loop
  if (!retry) {
    clearSessionAndRedirect();
    throw new Error("Unauthorized");
  }

  // Access token expired → refresh (shared single-flight) and retry once.
  let refreshedTokens;
  try {
    refreshedTokens = await refreshAuthToken();
  } catch (e) {
    clearSessionAndRedirect();
    throw e;
  }

  return fetchWithTokenRefresh(
    url,
    {
      ...options,
      headers: buildAuthorizedHeaders(options.headers || {}, refreshedTokens.accessToken),
    },
    false
  );
};


